import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { ICategoryData } from "@housebets/housebets-x-package/lib/components/feature/wallet";

import {useAppSelector} from "hooks/useAppSelector";
import { setWalletType } from "app/store/slices/setting";
import { CurrencyType, TKey, WalletType } from "app/types/common";
import { getCryptoFullName, getCurrencyIcon } from "app/utils/help";
import { CurrencyConfig } from "app/components/features/wallet";
import pkg from 'lodash';
import { useDispatch } from "react-redux";
const {debounce, sortBy} = pkg;

export const useWalletModalData = () => {
    const walletType = useAppSelector((state) => state.setting.walletType);
    const currencyList = useAppSelector((state) => state.config.platformConfig?.currencyList || []);
    const [activeTabIdx, setActiveTabIdx] = useState<number>(0);
    const [activeItem, setActiveItem] = useState<ICategoryData | undefined>();
    const [selectedCountry, setSelectedCountry] = useState<TKey[]>(['en']);
    const dispatch = useDispatch();

    const { $t } = useIntl();

    const closeWalletModal = useCallback(() => {
        dispatch(setWalletType(WalletType.Hide));
    }, []);

    const title = useMemo(() => {
        if (!activeItem) return $t({ id: "Wallet" });

        if (activeTabIdx === 0 && activeItem?.category === "crypto") return $t({ id: "DepositCrypto" });
        if (activeTabIdx === 0 && activeItem?.category === "cash") return $t({ id: "DepositCash" });
        if (activeTabIdx === 0 && activeItem?.category === "gift_cards") return $t({ id: "DepositCart" });
        if (activeTabIdx === 1 && activeItem?.category === "crypto") return $t({ id: "WithdrawCrypto" });
        if (activeTabIdx === 1 && activeItem?.category === "cash") return $t({ id: "WithdrawCash" });
        if (activeTabIdx === 2) return $t({ id: "BuyCrypto" });
        if (activeTabIdx === 3) return $t({ id: "Tip" });

        return $t({ id: "Wallet" });
    }, [activeItem, activeTabIdx]);

    const cryptoData = useMemo(() => {
        const sortedArr = sortBy(
            currencyList.filter((c) => c.categoryId === CurrencyType.Crypto && c.canDeposit === 1),
            (item) => item.listOrder,
        );
        const res: ICategoryData[] = [];
        sortedArr.forEach((v) => {
            const temp: ICategoryData = {
                title: getCryptoFullName(v.tokenName),
                id: v.tokenId,
                category: "crypto",
                code: v.tokenName,
                image: getCurrencyIcon(v.tokenId)
            };
            res.push(temp);
        });
        return res;
    }, [currencyList]);

    const fiatData = useMemo(() => {
        const sortedArr = sortBy(
            currencyList.filter((c) => c.categoryId === CurrencyType.Fiat && c.canDeposit === 1),
            (item) => item.listOrder,
        );
        const res: ICategoryData[] = [];
        sortedArr.forEach((v) => {
            const temp: ICategoryData = {
                title: getCryptoFullName(v.tokenName),
                id: v.tokenId,
                category: "cash",
                code: v.tokenName,
                image: getCurrencyIcon(v.tokenId)
            };
            res.push(temp);
        });
        return res.filter((item) => CurrencyConfig.find((c) => c.type === item?.id));
    }, [currencyList]);

    useEffect(() => {
        if (walletType !== WalletType.Hide) {
            setActiveItem(window.innerWidth < 1024 ? undefined : cryptoData?.[0]);
            setActiveTabIdx(0);
        }
    }, [walletType]);

    return {
        walletType,
        closeWalletModal,
        activeTabIdx,
        setActiveTabIdx,
        activeItem,
        setActiveItem,
        selectedCountry,
        setSelectedCountry,
        cryptoData,
        fiatData,
        title
    }
}